import React from "react"
import { Plus, Trash2, X } from "lucide-react"
import { z } from "zod"
import { toast } from "sonner"

import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Label } from "@/components/atoms/Label/label"
import { Button } from "@/components/atoms/Button/button"
import { ZodErrors } from "@/components/molecules/ZodError/zod-error"
import { HeadingText } from "@/components/atoms/HeadingText/heading-text"
import { useModalStore } from "@/hooks/useModal"
import { Dialog, DialogContent } from "@/components/molecules/Dialog/content"
import { DialogHeader } from "@/components/molecules/Dialog/header"
import { addCategoryAction } from "@/actions/product/product.action"
import { useFormManager } from "@/app/hooks/useFormManager"

// Define the validation schema with Zod
const categorySchema = z.object({
  name: z.string().min(1, "Category name is required"),
  subCategories: z
    .array(z.object({ name: z.string().min(1, "Subcategory name is required") }))
    .min(1, "At least one subcategory is required"),
})

// Define the TypeScript type from the Zod schema
export type NewCategory = z.infer<typeof categorySchema>

// The form component
const ProductCategoryForm: React.FC = () => {
  const { modals, setNewlyCreatedCategoryId, closeModal } = useModalStore()

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<NewCategory>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      subCategories: [{ name: "" }], // Ensure one default subcategory as an object
    },
  })

  // Consolidated Form Management
  const { clearAfterSuccess } = useFormManager({
    formName: "product-category",
    form: { register, handleSubmit, control, reset, formState: { errors, isSubmitting, isDirty: false } as any } as any,
    defaultValues: {
      name: "",
      subCategories: [{ name: "" }],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subCategories",
  })

  // Form submission handler
  const onSubmit = async (data: NewCategory) => {
    try {
      const response: any = await addCategoryAction(data)
      const newCategoryId = response?.data?.data?.id
      if (newCategoryId) setNewlyCreatedCategoryId(newCategoryId)
      clearAfterSuccess()
      toast.success("Product category added successfully.")
      closeModal("product-category")
    } catch (error) {
      toast.error("There was an error submitting the form. Please try again.")
    }
  }

  // Handlers for adding/removing subcategories
  const handleAddSubcategory = () => append({ name: "" }) // Properly append with object structure
  const handleRemoveSubcategory = (index: number) => {
    if (fields.length > 1) {
      remove(index)
    }
  }

  return (
    <Dialog open={!!modals["product-category"]} onOpenChange={() => closeModal("product-category")}>
      <DialogContent className="mx-auto bg-white p-4 dark:bg-gray-900 sm:max-w-[625px]">
        <div className="flex justify-between">
          <HeadingText level={3}>Add Category</HeadingText>
          <DialogHeader>
            <div className="mb-4 flex justify-end">
              <Button variant="ghost" onClick={() => closeModal("product-category")}>
                <X className="text-gray-500" size={25} />
              </Button>
            </div>
          </DialogHeader>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Category Name</Label>
            <input
              id="name"
              type="text"
              {...register("name")}
              className="dashboard-input w-full"
              placeholder="Enter category name"
            />
            <ZodErrors id="categoryNameError" error={errors.name?.message ? [errors.name.message] : []} />
          </div>

          <div className="space-y-2">
            <Label>Subcategories</Label>
            {fields.map((field, index) => (
              <div className="flex flex-col" key={field.id}>
                <div className="flex gap-2">
                  <input
                    {...register(`subCategories.${index}.name` as const)}
                    type="text"
                    className="dashboard-input w-full"
                    placeholder={`Subcategory ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSubcategory(index)}
                    className="p-2 text-red-600 hover:text-red-700"
                    aria-label={`Remove subcategory ${index + 1}`}
                    disabled={fields.length === 1} // Prevents deleting the last subcategory
                  >
                    <Trash2 />
                  </button>
                </div>
                <ZodErrors
                  id={`subCategories.${index}.nameError`}
                  error={
                    errors.subCategories?.[index]?.name?.message ? [errors.subCategories[index]?.name?.message] : []
                  }
                />
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddSubcategory}
              className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700"
            >
              <Plus className="mr-1" /> Add Subcategory
            </button>
          </div>

          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => closeModal("product-category")}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md !bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:!bg-blue-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Add Category"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ProductCategoryForm
